/**
 * AuditLevelSelector — Composant partagé pour la sélection des 3 niveaux d'audit
 * Confilex (29 / 39 / 49 €).
 *
 * Architecture unique des audits : utilisé par Dossier (Cas n°1), Vérification
 * de documents (Cas n°2) et Négociation (Cas n°3).
 *
 * Props :
 *   open          (bool)            — affichage modal
 *   onClose       (fn)              — fermeture
 *   onConfirm     (fn(level))       — palier choisi (essentiel|avance|premium)
 *   flow          ('dossier'|'verifier'|'negotiation') — contexte affichage
 *   levels        (Array | null)    — surcharge optionnelle (sinon fetch /api/dossier/audit/levels)
 *   expertCabinet (string?)         — nom du cabinet ciblé (Cas n°1)
 *   loading       (bool)            — état d'envoi vers Stripe
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, ShieldCheck, X, Sparkles, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditLevelSelector({
  open, onClose, onConfirm, flow = "dossier",
  levels: levelsProp = null,
  expertCabinet = null,
  loading = false,
}) {
  const [levels, setLevels] = useState(levelsProp || []);
  const [picked, setPicked] = useState(null);
  const [consent, setConsent] = useState(false);
  const [waiver, setWaiver] = useState(false);
  const [meta, setMeta] = useState({ disclaimer: "", waiver_14_days_notice: "" });
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open || levelsProp) return;
    let cancelled = false;
    setFetching(true);
    axios.get(`${API}/dossier/audit/levels`).then((r) => {
      if (cancelled) return;
      setLevels(r.data?.levels || []);
      setMeta({
        disclaimer: r.data?.disclaimer || "",
        waiver_14_days_notice: r.data?.waiver_14_days_notice || "",
      });
    }).catch(() => {}).finally(() => !cancelled && setFetching(false));
    return () => { cancelled = true; };
  }, [open, levelsProp]);

  useEffect(() => {
    if (open) {
      setPicked(null); setConsent(false); setWaiver(false);
    }
  }, [open]);

  if (!open) return null;

  const canConfirm = !!picked && consent && waiver && !loading;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-[#031B12]/85 backdrop-blur-md p-3 sm:p-6"
      data-testid="audit-level-selector"
      role="dialog"
      aria-modal="true"
      aria-label="Sélection du niveau d'audit Confilex"
    >
      <div className="cb-card w-full max-w-4xl max-h-[92vh] overflow-y-auto relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-[#031B12]/60 hover:bg-[#031B12] text-[#F7F7F2] transition"
          aria-label="Fermer"
          data-testid="audit-selector-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8 space-y-5">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#063D25]">
              <Sparkles className="w-5 h-5 cb-gold" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider cb-gold font-bold">
                Audit Confilex préparatoire
              </p>
              <h2 className="cb-serif text-2xl sm:text-3xl font-semibold cb-text-ivory mt-1 leading-tight">
                Choisissez le niveau adapté à votre dossier
              </h2>
              {expertCabinet && (
                <p className="text-sm text-[#BFC8C2] mt-1">
                  Audit préalable à la transmission à <span className="cb-text-ivory font-semibold">{expertCabinet}</span>
                </p>
              )}
            </div>
          </div>

          {/* Cards 3 niveaux */}
          {fetching ? (
            <div className="text-center py-12 text-[#BFC8C2] flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement des paliers…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="audit-levels-grid">
              {levels.map((lvl) => {
                const active = picked === lvl.code;
                return (
                  <button
                    key={lvl.code}
                    type="button"
                    onClick={() => setPicked(lvl.code)}
                    data-testid={`audit-level-${lvl.code}`}
                    className={`text-left p-5 rounded-xl border transition-all relative ${
                      active
                        ? "border-[#F5C400] bg-[#063D25] ring-2 ring-[#F5C400]/30"
                        : "border-[#063D25] bg-[#031B12] hover:border-[#F5C400]/60"
                    }`}
                  >
                    {lvl.recommended && (
                      <span className="absolute -top-2 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F5C400] text-[#031B12]">
                        Recommandé
                      </span>
                    )}
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <span className="cb-serif text-lg font-semibold cb-text-ivory">{lvl.label}</span>
                      <span className="cb-gold font-bold text-xl">{lvl.price_eur} €</span>
                    </div>
                    <p className="text-xs text-[#BFC8C2] leading-snug mb-3">{lvl.description}</p>
                    <ul className="space-y-1">
                      {(lvl.deliverables || []).slice(0, 6).map((d, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#F7F7F2]/85">
                          <CheckCircle2 className="w-3 h-3 cb-gold shrink-0 mt-0.5" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                    {active && (
                      <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-[#F5C400] text-[#031B12] flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Consents */}
          <div className="space-y-2.5 pt-2 border-t border-[#063D25]">
            <label className="flex items-start gap-2.5 text-xs text-[#BFC8C2] cursor-pointer" data-testid="audit-consent-disclaimer-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="audit-consent-disclaimer"
                className="mt-0.5"
              />
              <span>
                J'accepte la <span className="cb-gold">clause de non-responsabilité</span> Confilex.
                {meta.disclaimer && (
                  <span className="block text-[10px] mt-1 opacity-75">{meta.disclaimer}</span>
                )}
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-xs text-[#BFC8C2] cursor-pointer" data-testid="audit-consent-waiver-label">
              <input
                type="checkbox"
                checked={waiver}
                onChange={(e) => setWaiver(e.target.checked)}
                data-testid="audit-consent-waiver"
                className="mt-0.5"
              />
              <span>
                Je <span className="cb-gold">renonce expressément à mon délai de rétractation de 14 jours</span> (Art. L221-28 1° Code conso) afin de recevoir le rapport immédiatement après paiement.
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3 border-t border-[#063D25]">
            <p className="text-[11px] text-[#BFC8C2] flex-1 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 cb-gold" /> Paiement sécurisé Stripe · CB, Apple Pay, Google Pay
            </p>
            <button
              type="button"
              onClick={() => canConfirm && onConfirm(picked)}
              disabled={!canConfirm}
              data-testid="audit-selector-confirm"
              className={`cb-btn-gold text-sm inline-flex items-center justify-center gap-1.5 px-5 py-2.5 ${
                canConfirm ? "" : "opacity-50 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</>
              ) : (
                <>Payer et lancer l'audit <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
